<template>
  <li
    class="section-item"
    :class="{ wide: isSideNavOpen, open: isOpen }"
  >
    <router-link
      v-if="section.to"
      :to="{
        name: section.to,
        key: section.inviteKey ? section.inviteKey : undefined,
      }"
      class="section-text text-body-1 link"
      @click="trackNavigation(section.to)"
    >
      {{ section.title }}
    </router-link>
    <div
      v-else-if="!section.to && !isSideNavOpen"
      class="dropdown-trigger"
    >
      <button
        v-if="section.isButton"
        type="button"
        class="section-text text-body-1"
        @click="emit('open-modal')"
      >
        {{ section.title }}
      </button>
      <span v-else class="section-text text-body-1 no-hover">
        {{ section.title }}
      </span>
    </div>
    <div
      v-else
      class="dropdown-trigger"
      @click="emit('section-toggle', section.id)"
    >
      <button
        v-if="section.isButton"
        type="button"
        class="section-text text-body-1"
        @click="emit('open-modal')"
      >
        {{ section.title }}
      </button>
      <span v-else class="section-text text-body-1">
        {{ section.title }}
      </span>
      <v-icon
        v-if="section.links"
        icon="$chevron-down"
        :size="20"
        class="dropdown-chevron mr-2"
      />
    </div>
    <ul
      v-if="section.links"
      class="dropdown-list"
      :class="{ open: isOpen }"
    >
      <li
        v-for="(link, linkIdx) in section.links"
        :key="linkIdx"
        class="dropdown-item"
      >
        <router-link
          v-if="link.to"
          :to="{
            name: link.to,
            query: link.query || {
              ...getQueryParam(link.to),
              key: link.inviteKey ? link.inviteKey : undefined,
            },
          }"
          class="dropdown-text text-body-2 link"
          @click="trackNavigation(link.to)"
        >
          {{ link.title }}
        </router-link>
        <span v-else class="dropdown-text text-body-2">
          {{ link.title }}
        </span>
      </li>
    </ul>
  </li>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { getReportingQueryParam } from '@/api/helpers/insights/insights';
import {
  postLibraryModes,
  reqLibraryModes,
  jobProfileLibraryModes,
  libraryQueryParams,
} from '@/components/library/helpers';

defineProps({
  section: {
    type: Object,
    default: () => ({}),
  },
  isOpen: {
    type: Boolean,
    default: true,
  },
  isSideNavOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['tracking-event', 'section-toggle', 'open-modal']);

const route = useRoute();

const libraryRouteNames = computed(() => ([
  ...postLibraryModes,
  ...reqLibraryModes,
  ...jobProfileLibraryModes,
]));

const getQueryParam = (routeName) => {
  if (libraryRouteNames.value.includes(routeName)) {
    return libraryQueryParams(route, routeName);
  }
  return getReportingQueryParam(route, routeName);
};

const trackNavigation = (routeName) => {
  emit('tracking-event', { action: 'navLinkClick', args: { label: routeName } });
};
</script>

<style lang="less" scoped>
.section-item:has(.router-link-exact-active):not(.open),
.section-item:has(> .router-link-exact-active.link),
.dropdown-item:has(.router-link-exact-active) {
  background-color: @bg-dark;
  border: 1px solid @stroke-default;
}

.dropdown-list {
  margin-left: 24px;
  border-left: 1px solid @stroke-default;
  padding-left: 8px;
  display: none;

  &.open {
    display: block;
  }
}

.section-item {
  border-radius: 8px;
  margin-top: 4px;
}

.section-item .dropdown-trigger:has(.section-text:not(.no-hover)),
.section-item:has(a.section-text) {
  border-radius: 8px;

  &:hover {
    cursor: pointer;
    background-color: @bg-dark;
  }
}

.section-item .dropdown-trigger {
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  :deep(svg path) {
    fill: @icon-default;
  }
}

.dropdown-item {
  border-radius: 8px;
  margin-top: 4px;

  &:hover {
    cursor: pointer;
    background-color: @bg-dark;
  }
}

.section-text, .dropdown-text {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  width: 100%;
  text-align: left;
  text-transform: capitalize;
  color: @text-darkest;
  height: 100%;
}

.section-text {
  padding: 4px 12px;
}

// for chevron animation
.dropdown-chevron {
  transition: 300ms all ease-in-out;
}

.section-item.open .dropdown-chevron {
  transform: rotateZ(180deg);
}
</style>
